<template>
  <div class="gw">
    <div class="gw__body">
      <section class="gw__status" :class="{ 'gw__status--on': active }">
        <span class="gw__logo" :style="{ background: tint }">
          <v-icon color="white">{{ icon }}</v-icon>
        </span>
        <div class="gw__heading">
          <div class="gw__title">
            {{ title }}
            <span class="gw__badge" :class="{ 'gw__badge--on': active }">
              <span class="gw__dot" />{{ active ? $t('active') : $t('deactive') }}
            </span>
          </div>
          <div v-if="description" class="gw__desc">{{ description }}</div>
        </div>
        <button
          type="button"
          class="gw__toggle"
          role="switch"
          :aria-checked="active ? 'true' : 'false'"
          :aria-label="title"
          @click="$emit('toggle', !active)"
        >
          <v-switch
            :input-value="active"
            class="gw__switch"
            color="primary"
            inset
            hide-details
            readonly
            dense
          />
        </button>
      </section>

      <div class="gw__sections" :class="{ 'gw__sections--off': !active }">
        <div v-if="!active" class="gw__off-note">
          <v-icon small>mdi-information-outline</v-icon>
          {{ $t('settingGatewayOffHint') }}
        </div>
        <slot />
      </div>
    </div>

    <footer class="gw__foot">
      <v-btn color="primary" depressed class="app-form-action-btn gw__submit" :loading="loading" @click="$emit('submit')">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('submit') }}
      </v-btn>
    </footer>
  </div>
</template>

<script>
// Shared frame for payment gateway settings: status card with on/off switch,
// field sections (dimmed while off), fixed footer with a single submit.
// Sections are provided by the gateway via the default slot using
// .gw-section / .gw-section__head / .gw-grid markup.
export default {
  name: 'GatewaySettingsLayout',
  props: {
    icon: { type: String, required: true },
    title: { type: String, required: true },
    description: { type: String, default: '' },
    tint: { type: String, default: '#1e88e5' },
    active: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
}
</script>

<style scoped>
.gw {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.gw__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.gw__body > * { flex-shrink: 0; }

/* Status card */
.gw__status {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}
.gw__status--on {
  border-color: rgba(46, 125, 50, 0.3);
  background: linear-gradient(0deg, rgba(46, 125, 50, 0.03), rgba(46, 125, 50, 0.03)), #fff;
}
.theme--dark .gw__status { background: rgba(255, 255, 255, 0.03); border-color: rgba(255, 255, 255, 0.12); }
.gw__logo {
  flex: 0 0 48px;
  height: 48px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}
.gw__heading { flex: 1 1 auto; min-width: 0; }
.gw__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 1rem;
  font-weight: 700;
}
.gw__desc {
  margin-top: 4px;
  font-size: 0.78rem;
  line-height: 1.6;
  opacity: 0.6;
}
.gw__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 10px;
  border-radius: 11px;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(15, 23, 42, 0.06);
  color: rgba(15, 23, 42, 0.6);
}
.gw__badge--on {
  background: rgba(46, 125, 50, 0.12);
  color: #2e7d32;
}
.gw__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.gw__toggle {
  flex: 0 0 auto;
  padding: 4px;
  border-radius: 8px;
  -webkit-tap-highlight-color: transparent;
}
.gw__toggle:focus-visible { outline: 2px solid var(--v-primary-base, #1976d2); }
.gw__switch { margin: 0 !important; padding: 0 !important; pointer-events: none; }

/* Sections (markup comes from the gateway through the slot) */
.gw__sections {
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: opacity 0.2s ease;
}
.gw__sections--off > :not(.gw__off-note) { opacity: 0.55; }
.gw__off-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  background: rgba(251, 140, 0, 0.08);
  color: #b26a00;
}
.gw__off-note .v-icon { color: inherit !important; }
.gw__sections ::v-deep .gw-section {
  padding: 14px 16px 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  background: #fff;
}
.theme--dark .gw__sections ::v-deep .gw-section { background: rgba(255, 255, 255, 0.03); border-color: rgba(255, 255, 255, 0.12); }
.gw__sections ::v-deep .gw-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.gw__sections ::v-deep .gw-section__title { font-size: 0.88rem; font-weight: 700; }
.gw__sections ::v-deep .gw-section__hint { font-size: 0.75rem; opacity: 0.6; margin-inline-start: auto; }
.gw__sections ::v-deep .gw-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 14px;
  align-items: start;
}
.gw__sections ::v-deep .gw-grid > * { margin: 0 !important; padding: 0 !important; min-width: 0; }
.gw__sections ::v-deep .gw-grid > .gw-span { grid-column: 1 / -1; }
.gw__sections ::v-deep .v-input { height: auto !important; }

/* Footer */
.gw__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  background: #fafbfd;
}
.theme--dark .gw__foot { background: rgba(255, 255, 255, 0.03); }

@media (max-width: 799px) {
  .gw__body { padding: 12px; gap: 12px; }
  .gw__status { padding: 14px 12px; gap: 12px; }
  .gw__logo { flex-basis: 42px; height: 42px; }
  .gw__sections ::v-deep .gw-section { padding: 12px; }
  .gw__sections ::v-deep .gw-grid { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .gw__foot { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); }
  .gw__submit {
    flex: 1 1 auto;
    height: 50px !important;
    border-radius: 14px !important;
    font-size: 0.95rem !important;
    font-weight: 700 !important;
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.28) !important;
  }
}
</style>
